import { useMemo } from "react";
import { Autocomplete, InputAdornment, TextField } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { getBrandSearchOptions } from "./utils/helpersOfBrands";

interface Props {
  rows: any[];
  value: string;
  onChange: (value: string) => void;
}

// Same brand-name search UX as the legacy Brand Configuration Report module
// (BrandConfigReportTableFilterCard.tsx): a freeSolo MUI Autocomplete with
// live-filter-as-you-type suggestions, a clear button that's always visible
// once there's a value, and Enter to close the dropdown. No ticked/multi-select
// state (that's All Brands' BrandDirectorySearch instead) — single value,
// substring match. Options are labeled "Brand Name (Brand ID)" (matching the
// legacy module's suggestion format) so the user can search by either name or
// id — matchesBrandNameOrId (helpersOfBrands.ts) does the actual row
// filtering against this same combined format. Used on Needs Configuration
// and Not Connected, per spec.
const BrandNameSearch = ({ rows, value, onChange }: Props) => {
  const brandNameOptions = useMemo(() => getBrandSearchOptions(rows), [rows]);

  return (
    <Autocomplete
      fullWidth
      disablePortal
      freeSolo
      sx={{
        width: 390,
        "& .MuiAutocomplete-clearIndicator": {
          visibility: value ? "visible" : "hidden",
        },
      }}
      options={brandNameOptions}
      value={value || ""}
      disableClearable={!value}
      onInputChange={(_, newVal) => onChange(newVal ?? "")}
      onChange={(_, newValue) => onChange((newValue as string) ?? "")}
      getOptionLabel={(opt) => opt || ""}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Brand Name"
          size="small"
          color="secondary"
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              (e.target as HTMLInputElement).blur();
            }
          }}
        />
      )}
      size="small"
    />
  );
};

export default BrandNameSearch;
